import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { metaAdsApi } from '../utils/api';

/**
 * Hook de gestion de la liste des audiences Meta (personnalisées et similaires).
 *
 * Source unique de vérité : l'onglet « Audiences » et l'écran de revue du brouillon
 * partagent la même liste. Auparavant chacun chargeait la sienne au montage, si bien
 * qu'un rafraîchissement dans un onglet ne se répercutait pas dans l'autre : une
 * audience devenue diffusable restait grisée dans le ciblage, et les audiences créées
 * entre-temps n'y apparaissaient pas.
 *
 * Le hook n'impose aucun affichage : il expose la liste, l'état de chargement et une
 * fonction de rechargement, à charge de l'appelant de les présenter comme il le souhaite.
 *
 * @param {object} [options]
 * @param {boolean} [options.silencieux=false] - Ne pas notifier l'utilisateur en cas
 *   d'échec. Utile là où les audiences sont accessoires (revue du brouillon) et où une
 *   erreur ne doit pas interrompre la tâche en cours.
 * @returns {{ audiences: object[], loading: boolean, rafraichir: function }}
 */
const useAudiencesMeta = ({ silencieux = false } = {}) => {
  const [audiences, setAudiences] = useState([]);
  const [loading, setLoading] = useState(false);
  // Erreur conservée pour l'appelant qui souhaiterait l'afficher lui-même
  const [erreur, setErreur] = useState(null);

  const rafraichir = useCallback(async () => {
    try {
      setLoading(true);
      setErreur(null);
      const { data } = await metaAdsApi.getAudiences();
      setAudiences(data.data || []);
      return data.data || [];
    } catch (error) {
      const messageErreur = error.response?.data?.message || 'Erreur lors du chargement des audiences';
      setErreur(messageErreur);
      // Un compte sans audience ne doit pas empêcher la revue d'un brouillon
      if (silencieux) {
        console.warn('Audiences Meta indisponibles:', error.message);
      } else {
        console.error('Erreur lors du chargement des audiences Meta:', error);
        message.error(messageErreur);
      }
      return [];
    } finally {
      setLoading(false);
    }
  }, [silencieux]);

  // Chargement initial
  useEffect(() => {
    rafraichir();
  }, [rafraichir]);

  return { audiences, loading, erreur, rafraichir };
};

export default useAudiencesMeta;
